import { useState, useEffect } from 'react';

import { getScrollPosition } from '@/components/utils/layoutOptimization';

/**
 * Hook for detecting scroll direction and hiding/showing header
 */
export const useScrollBehavior = (currentPageName: string) => {
  const [scrollY, setScrollY] = useState(0);
  const [isScrollingDown, setIsScrollingDown] = useState(false);
  const [shouldHideHeader, setShouldHideHeader] = useState(false);

  useEffect(() => {
    let lastScrollY = getScrollPosition();
    let ticking = false;

    const updateScrollState = () => {
      const currentScrollY = getScrollPosition();

      // Don't hide header on Home page - always keep it visible
      if (currentPageName === 'Home') {
        setShouldHideHeader(false);
        setIsScrollingDown(currentScrollY > lastScrollY);
      } else {
        // Only hide if scrolled down more than 100px on other pages
        if (currentScrollY > 100) {
          setIsScrollingDown(currentScrollY > lastScrollY);
          setShouldHideHeader(currentScrollY > lastScrollY);
        } else {
          setShouldHideHeader(false);
        }
      }

      setScrollY(currentScrollY);
      lastScrollY = currentScrollY;
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateScrollState);
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [currentPageName]);

  return { scrollY, isScrollingDown, shouldHideHeader };
};

/**
 * Hook to close dropdowns when scrolling
 */
export const useCloseOnScroll = (onClose: () => void) => {
  useEffect(() => {
    const handleScroll = () => {
      onClose();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [onClose]);
};
